import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrgUnitDecision } from "@/api/diagnostic-org-structure";
import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

import { useDecisionLabels, type DecisionView } from "./decisionLabels";

/** A ordem do seletor (D13). Pendente é uma escolha válida, por isso o seletor começa vazio. */
const OPTIONS: readonly DecisionView[] = ["in_scope", "client_validation", "out_of_scope", "pending"];

/**
 * A barra do lote: quantas unidades estão selecionadas, o escopo e Aplicar.
 * Aplicar fica desabilitado sem escopo escolhido e enquanto o lote grava, para o
 * duplo clique não mandar dois lotes.
 */
export function OrgBatchBar({
  count,
  saving,
  onApply,
}: {
  count: number;
  saving: boolean;
  onApply(decision: OrgUnitDecision | null): void;
}) {
  const { t } = useTranslation();
  const decisions = useDecisionLabels();
  const [choice, setChoice] = useState<DecisionView | "">("");

  return (
    <div
      data-testid="org-batch-bar"
      className="flex flex-wrap items-center gap-3 border-b border-[#eef1f5] bg-[#f5f8fc] px-3 py-2"
    >
      <span className="text-[12px] font-semibold text-[#08254c]">
        {t("Selected: {{total}}", { total: formatCount(count) })}
      </span>
      <select
        aria-label={t("Scope for the selected units")}
        value={choice}
        onChange={(event) => setChoice(event.target.value as DecisionView | "")}
        className="rounded-md border border-[#d4dae3] bg-white px-2 py-1 text-[12px] text-[#08254c]"
      >
        <option value="">{t("Choose the scope")}</option>
        {OPTIONS.map((option) => (
          <option key={option} value={option}>
            {decisions[option]}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={choice === "" || saving}
        onClick={() => {
          if (choice !== "") onApply(choice === "pending" ? null : choice);
        }}
        className={cn(
          "rounded-md border border-[#08254c] bg-[#08254c] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[#0d3366]",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        {t("Apply")}
      </button>
    </div>
  );
}
